import { cx } from "@suse-o11y/class-variance";
import { useDialogClose } from "@suse-o11y/dialog";
import { IconInfoItalicOutlined } from "@suse-o11y/icons";
import { matchIdentifiable } from "@suse-o11y/identifiable";
import { not } from "@suse-o11y/not";
import { onIdle } from "@suse-o11y/on-idle";
import React from "react";

import { Checkbox } from "~/components/checkbox";
import { CheckboxLabel } from "~/components/checkbox/label";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormMessage } from "~/components/form/message";
import { FormFieldReset } from "~/components/form/reset";
import { Input } from "~/components/input";
import { Star } from "~/components/star";
import { Textarea } from "~/components/textarea";

import { queryAllDashboards } from "../../queries/dashboards";
import { useQuery } from "../../queries/react-query";
import { DASHBOARD_PATH } from "../../routes/Routes";
import { ErrorMessage } from "../_notification-error";
import { useNavigateTo } from "../react/link/hooks/useNavigateTo";
import { LinkTo } from "../react/link/LinkTo";
import { DONT_AUTO_DISMISS, notification } from "../react/notification/Notification";
import { HelpPopupTooltip } from "../react/tooltip/HelpPopupTooltip";

import type { FormState } from "./_form-state";
import { useSetupDashboard } from "./_use-setup-dashboard";
import type { SetupDashboardDrawerVariant } from "./variant";

export interface SetupDashboardFormProps {
	formId: string;
	variant: SetupDashboardDrawerVariant;
	form: FormState;
	className?: string;
}

export const SetupDashboardForm: React.FC<SetupDashboardFormProps> = ({ formId, variant, form, className }) => {
	const readonly = variant.type === "edit" && (variant.readonly ?? false);

	const close = useDialogClose();
	const navigateTo = useNavigateTo();
	const nameInputRef = React.useRef<HTMLInputElement>(null);

	const { data: usedDashboardNames } = useQuery({
		...queryAllDashboards,
		// do not refetch - the cached data is alright
		refetchOnMount: false,
		select: (dashboards) => {
			const otherDashboards =
				variant.type === "edit" ? dashboards.filter(not(matchIdentifiable(variant.id))) : dashboards;

			return new Set(otherDashboards.map(({ name }) => name));
		},
	});

	const { createDashboard, cloneDashboard, editDashboard } = useSetupDashboard({
		onSuccessfulCreate: (dashboard) => {
			if (variant.type === "create" && variant.onSuccess != null) {
				variant.onSuccess(dashboard);
			} else {
				navigateTo(DASHBOARD_PATH, {
					params: { idOrIdentifier: dashboard.id },
					search: ["timeRange", "timestamp", "detachedFilters"],
				});
			}
		},

		onSuccessfulClone: ({ id, name }) => {
			notification.success({
				message: <SuccessfulCloneMessage dashboardId={id} dashboardName={name} />,
			});
		},

		onSuccessfulEdit: (name) => {
			notification.success({
				message: `Dashboard "${name}" has been successfully updated.`,
			});
		},

		onSuccessfulStar: () => {
			notification.success({
				message: "Dashboard has been successfully updated.",
			});
		},

		onFailedStar: (error) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: <ErrorMessage error={error}>Something went wrong while starring a dashboard</ErrorMessage>,
			});
		},
	});

	React.useEffect(() => {
		// the drawer does not allow to autoFocus the input
		// neither .focus() during synchronous effect
		return onIdle(() => {
			nameInputRef.current?.focus();
		});
	}, []);

	return (
		<Form
			className={cx("space-y-6", className)}
			id={formId}
			onSubmit={async (values) => {
				if (variant.type === "create") {
					try {
						await createDashboard(variant.widgets ?? [], values);
						// wait for the dashboard to be created before closing
						close();
					} catch {
						// the failed Promise is handled in the hook
					}
				} else if (variant.type === "clone") {
					try {
						await cloneDashboard(variant.id, variant.spec, values);
						// wait for the dashboard to be cloned before closing
						close();
					} catch {
						// the failed Promise is handled in the hook
					}
				} else {
					editDashboard(variant.id, form, values);

					// close the drawer immediately
					close();
				}
			}}
			state={form}
		>
			<FormField state={form.fields.dashboard.fields.name}>
				<FormLabel asChild>
					<div>
						<FormLabel>Name *</FormLabel>
						{variant.type !== "create" && <FormFieldReset />}
					</div>
				</FormLabel>

				{(monitor, field) => (
					<Input
						name="dashboard-name"
						onChange={(event) => field.setInput(event.target.value)}
						placeholder="Keep it short and sweet"
						readOnly={readonly}
						ref={nameInputRef}
						value={field.getInput(monitor)}
					/>
				)}

				{(monitor, field) => {
					// when creating show only when the field is validated
					if (variant.type === "create" && !field.isValidated(monitor)) {
						return null;
					}

					const dashboardName = field.getOutput(monitor);

					if (dashboardName == null || !usedDashboardNames?.has(dashboardName)) {
						return null;
					}

					return (
						<FormMessage className="flex items-center gap-1 font-light text-grey-600 italic">
							<IconInfoItalicOutlined className="text-(--base-blue-600)" />
							This name is already in use
						</FormMessage>
					);
				}}
			</FormField>

			<FormField state={form.fields.dashboard.fields.description}>
				<FormLabel asChild>
					<div>
						<FormLabel>Description</FormLabel>
						{variant.type !== "create" && <FormFieldReset />}
					</div>
				</FormLabel>

				{(monitor, field) => (
					<Textarea
						name="dashboard-description"
						onChange={(event) => field.setInput(event.target.value)}
						placeholder="e.g. standard DevOps investigation widgets"
						readOnly={readonly}
						rows={5}
						value={field.getInput(monitor)}
					/>
				)}
			</FormField>

			<div className="space-y-3">
				<FormField state={form.fields.options}>
					<FormLabel asChild>
						<div>
							<FormLabel>Options</FormLabel>
							{variant.type !== "create" && <FormFieldReset />}
						</div>
					</FormLabel>
				</FormField>

				<div className="space-y-2">
					<FormField state={form.fields.options.fields.starred}>
						{(monitor, field) => {
							const starred = field.getInput(monitor);

							return (
								<CheckboxLabel className="flex items-center gap-2">
									<Checkbox
										checked={starred}
										disabled={readonly}
										name="dashboard-starred"
										onCheckedChange={(checked) => field.setInput(checked)}
									/>
									<span className="leading-[21px]">Add it to starred dashboards</span>
									<Star className="-m-0.5" starred={starred} />
								</CheckboxLabel>
							);
						}}
					</FormField>

					<FormField state={form.fields.options.fields.publiclyAccessible}>
						{(monitor, field) => {
							const publiclyAccessible = field.getInput(monitor);

							return (
								<div className="flex items-center gap-2">
									<CheckboxLabel className="flex items-center gap-2">
										<Checkbox
											checked={publiclyAccessible}
											disabled={readonly}
											name="dashboard-publicly-accessible"
											onCheckedChange={(checked) => field.setInput(checked)}
										/>
										<span className="leading-[21px]">Make it public</span>
										<HelpPopupTooltip content="By default, all the dashboards you create are private (only you can see them). By making a dashboard public, you still own the dashboard but other users can also view it." />
									</CheckboxLabel>
								</div>
							);
						}}
					</FormField>
				</div>
			</div>

			<blockquote className="m-0 text-13/[21px] text-grey-600 italic tracking-tight">
				<strong className="font-semibold">Pro tip</strong>
				<br />
				If you frequently use a dashboard, you should star it - all the starred dashboards are listed directly under the
				Dashboards section of the main menu for quick access.
			</blockquote>
		</Form>
	);
};

const SuccessfulCloneMessage: React.FC<{
	dashboardId: number;
	dashboardName: string;
}> = ({ dashboardId, dashboardName }) => (
	<>
		The dashboard has been successfully saved as "
		<LinkTo
			params={{ idOrIdentifier: dashboardId }}
			search={["timeRange", "timestamp", "detachedFilters"]}
			theme="secondary"
			to={DASHBOARD_PATH}
		>
			{dashboardName}
		</LinkTo>
		"
	</>
);
